import { useState, useEffect } from 'react';
import { checkFeatureFlag } from '../utils/FeatureFlagsUtils';

interface UseFeatureFlagResult {
  isEnabled: boolean;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Custom hook to check if a feature flag is enabled
 * @param flagName - The name of the feature flag to check
 * @returns An object containing the feature flag status, loading state, and error
 */
export const useFeatureFlag = (flagName: string): UseFeatureFlagResult => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const checkFlag = async () => {
      try {
        setIsLoading(true);
        const enabled = await checkFeatureFlag(flagName);
        setIsEnabled(enabled);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to check feature flag'));
        setIsEnabled(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkFlag();
  }, [flagName]);

  return { isEnabled, isLoading, error };
};
